import { IAngularEvent, IDirective, IDirectiveFactory, IPromise, IScope, equals } from "angular";
import { StateService } from "@uirouter/angularjs";
import { combineLatest, firstValueFrom, take } from "rxjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { ProgressColoring } from "@gtmhub/configuration/services/progress-coloring";
import { untilScopeDestroyed } from "@gtmhub/core/rxjs";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { IEmployee } from "@gtmhub/employees/models/models";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { HomeScreenView } from "@gtmhub/home/utils";
import { GoalsChangedEventArgs, IGoalChildReorderedEventDetail, OkrEvents } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { Ng1UiModalService } from "@gtmhub/shared/components/modal/ng1-modal.service";
import { ITeam } from "@gtmhub/teams";
import { IdMap, toIdMap } from "@gtmhub/util";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { AssignPermissions } from "@webapp/configuration/models/configuration.model";
import { ICollection } from "@webapp/core/core.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { GridHeaderColumn } from "@webapp/home/components/grid-header/grid-header.models";
import { localize } from "@webapp/localization/utils/localization.utils";
import { CreateOkrFormModalService } from "@webapp/okrs/components/create-okr-form/create-okr-form-modal.service";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { UpdateMetricModalService } from "@webapp/okrs/metrics/services/update-metric-modal.service";
import { GuidedOKRsComponent, GuidedOKRsModalData } from "@webapp/platform-intelligence/guided-okrs/guided-okrs.component";
import { PiFeatureVisibilityFacade } from "@webapp/platform-intelligence/shared/services/pi-feature-visibility-facade.service";
import { PiTrackingFlowNameEnum } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { Session } from "@webapp/sessions/models/sessions.model";
import { DefaultSessionResolverService } from "@webapp/sessions/services/default-session-resolver.service";
import dayjs from "@webapp/shared/libs/dayjs";
import { IHomeCollapsedItems } from "../controllers/dashboard";
import { HomeService } from "../services/home";

type IMyOKRs = IGoal | Metric;

type SessionsPermissions = {
  permissionsObjectives: AssignPermissions;
  permissionsKRs: AssignPermissions;
};

export interface IHomeGoal extends IGoal {
  parent?: IMyOKRs;
  isKRGoalPrivate?: boolean;
  isCurrentUserTheOwnerOfParent?: boolean;
  hideGoal?: boolean;
}

interface IParentFilter {
  type: string;
  fields: string;
}

interface IParentFilters {
  goalFilters: IParentFilter;
  metricFilters: IParentFilter;
}

export interface IHomeMyOkrsScope extends IScope {
  okrs: IGoal[];
  myOkrs: IHomeGoal[];
  teams: ITeam[];
  teamOkrs: ITeamOkr[];
  strings: {
    owner: {
      team: string;
      user: string;
    };
  };
  activeSessions: Session[];
  activeSessionsMap: IdMap<Session>;
  activeSessionIds: string[];
  employee: IEmployee;
  goalsAndMetricDeltasMap: IDeltaMap;
  currentState: string;
  selectedSession: Session;
  indicators: {
    loading?: IIndicator;
  };
  myOkrsCollapsed: boolean; // TODO This is inverted
  myTeamsOkrsCollapsed: { [id: string]: boolean }; // TODO This is inverted
  canCreateOKRs: boolean;
  permissionsObjectives: string;
  permissionsKRs: string;
  permissionsOKRUsersOnly: boolean;
  permissionsOKRTeamsOnly: boolean;
  hasAnyTeamOKRs: boolean;
  hasAnyUserOKRs: boolean;
  isGuidedOKRsCreationEnabled: boolean;
  myOkrsGridHeaders: GridHeaderColumn[];
  teamGridHeaders: GridHeaderColumn[];
  updateCollapse(item: { [key: string]: Partial<IHomeCollapsedItems> }): void;
  openSessionSelectorModal(team?: ITeam): void;
  openGoal(goal: IGoal): void;
  openMetric(metric: Metric): void;
  openUpdateMetricState(goal: IGoal, metric: Metric): void;
  updateMetric(goal: IGoal, metric: Metric): void;
  toggleSection(owner: "user" | "team", teamId?: string): void;
  isCollapsed(params: { owner: string; isMyListCollapsed: boolean; isTeamListCollapsed: boolean }): boolean;
  checkIfCurrentUserOwnsMetricParent(ownerIds: string[], params: { isTeamSection: boolean }): boolean;
  goToFeed(): void;
  openQuantivePlusWizard(): void;
}

interface IDeltaMap {
  [key: string]:
    | IGoal
    | Metric
    | {
        attainmentDelta: {
          current?: number;
          delta?: number;
          deltaPercentage?: number;
          previous?: number;
        };
        deltarange?: {
          from: string;
          to: string;
        };
        id?: string;
      };
}

interface ITeamOkr extends ITeam {
  goals: IHomeGoal[];
}

export interface IGoalEnriched extends IGoal {
  hideGoal: boolean;
}

export class HomeMyOkrs implements IDirective {
  public scope = {
    employee: "=",
    okrs: "=",
    teams: "=",
    activeSessionIds: "=",
    activeSessions: "=",
    myOkrsCollapsed: "<",
    myTeamsOkrsCollapsed: "<",
    updateCollapse: "&",
  };
  public restrict = "";
  public template = require("../views/my-okrs.html");

  private defaultHasOkrs = false;

  constructor(
    private homeService: HomeService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $state: StateService,
    private goalService: GoalService,
    private $rootScope: ITraceRootScopeService,
    private analytics: AnalyticsService,
    private progressColoring: ProgressColoring,
    private metricService: MetricService,
    private accountService: AccountService,
    private apmSpanService: ApmSpanService,
    private ng1UiModalService: Ng1UiModalService,
    private defaultSessionResolverService: DefaultSessionResolverService,
    private piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
    private updateMetricModalService: UpdateMetricModalService,
    private featureToggleFacade: FeatureTogglesFacade,
    private createOkrFormModalService: CreateOkrFormModalService
  ) {}

  private states = {
    msTeams: {
      goalSidebar: "gtmhub.msTeamsHome.dashboard._goals._goalSidebar.goal",
      metric: "gtmhub.msTeamsHome.dashboard._metric.metric",
      metricDetails: "gtmhub.msTeamsHome.dashboard._metric.metric._updateMetric.update",
      createGoal: "gtmhub.msTeamsHome.dashboard._goals._goalForm.form",
    },
    goalSidebar: "gtmhub.home.dashboard._goals._goalSidebar.goal",
    metric: "gtmhub.home.dashboard._metric.metric",
    metricDetails: "gtmhub.home.dashboard._metric.metric._updateMetric.update",
    createGoal: "gtmhub.home.dashboard._goals._goalForm.form",
  };

  private mongoFilters: IParentFilters = {
    goalFilters: {
      type: "goal",
      fields: "ownerIds,name,private",
    },
    metricFilters: {
      type: "metric",
      fields: "ownerIds,name,goalId",
    },
  };

  private $scope: IHomeMyOkrsScope = null;
  private okrEvents: OkrEvents;

  public link(myOkrsScope: IHomeMyOkrsScope): void {
    this.$scope = myOkrsScope;
    this.apmSpanService.dataLoadSpan("my-okrs-init", () => this.init());
  }

  private init = () => {
    this.setGoalAndMetricFeatureFlags();

    this.piFeatureVisibilityFacade
      .isGuidedOKRsCreationVisible$()
      .pipe(take(1))
      .subscribe((enabled) => (this.$scope.isGuidedOKRsCreationEnabled = enabled));

    this.okrEvents = new OkrEvents(this.$scope);
    this.$scope.strings = this.strings;
    this.$scope.indicators = {};
    this.$scope.activeSessionsMap = toIdMap(this.$scope.activeSessions);

    this.$scope.openGoal = this.openGoal;
    this.$scope.openMetric = this.openMetric;
    this.$scope.toggleSection = this.toggleSection;
    this.$scope.openSessionSelectorModal = this.openSessionSelectorModal;
    this.$scope.checkIfCurrentUserOwnsMetricParent = this.checkIfCurrentUserOwnsMetricParent;
    this.$scope.openQuantivePlusWizard = this.openQuantivePlusWizard;

    this.$scope.currentState = this.$state.current.name;

    this.$scope.$on("homeMyOkrsUpdated", this.onMyOkrsUpdated);
    this.$scope.$on("goalDeleted", this.onGoalDeletion);

    const { permissionsObjectives, permissionsKRs } = this.calculateSessionsPermissions();

    this.$scope.permissionsObjectives = permissionsObjectives;
    this.$scope.permissionsKRs = permissionsKRs;

    this.$scope.openUpdateMetricState = this.openUpdateMetricState;
    this.$scope.isCollapsed = this.isCollapsed;
    this.$scope.myOkrs = []; // default
    this.$scope.teamOkrs = []; // default
    this.$scope.goalsAndMetricDeltasMap = {}; // default

    this.$scope.hasAnyUserOKRs = this.defaultHasOkrs;
    this.$scope.hasAnyTeamOKRs = this.defaultHasOkrs;

    this.$scope.permissionsOKRUsersOnly = permissionsObjectives === "user" && permissionsKRs === "user";
    this.$scope.permissionsOKRTeamsOnly = permissionsObjectives === "team" && permissionsKRs === "team";

    this.$scope.canCreateOKRs = this.$rootScope.hasPermission("ManageGoals");
    this.$scope.goToFeed = this.goToFeed;

    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.$scope.myOkrsGridHeaders = [
      {
        label: localize("my_okrs"),
        flex: 5,
        alwaysVisible: true,
      },
      {
        label: localize("progress_cap"),
        flex: 3,
      },
      {
        label: localize("last_updated"),
        flex: 2,
      },
      this.$rootScope.featureAvailable("okrs.goal-states") && this.$rootScope.hasPermission("ManageGoals")
        ? {
            label: localize("okr_state"),
            flex: 3,
          }
        : null,
      this.$rootScope.hasPermission("ManageGoals")
        ? {
            label: localize("update_kr"),
            flex: 2,
          }
        : null,
    ].filter(Boolean);

    this.$scope.teamGridHeaders = [
      {
        label: localize("okrs"),
        flex: 5,
        alwaysVisible: true,
      },
      {
        label: localize("progress_cap"),
        flex: 3,
      },
      {
        label: localize("last_updated"),
        flex: 2,
      },
      this.$rootScope.featureAvailable("okrs.goal-states")
        ? {
            label: localize("okr_state"),
            flex: 3,
          }
        : null,
      {
        label: localize("update_kr"),
        flex: 2,
      },
    ].filter(Boolean);

    return this.$rootScope.traceInit(() => {
      if (this.$scope.okrs && this.$scope.okrs.length > 0) {
        this.$scope.indicators.loading = { progress: true };

        return this.setMetricsDeltas().then(() => this.scopeGoalsAndTrackScreen());
      } else {
        // we have no OKRs, but we need to display My OKRs and Team sections on the home page
        this.scopeGoalsAndTrackScreen();
      }
    });
  };

  private setGoalAndMetricFeatureFlags(): void {
    combineLatest({
      isA14MetricDrawerEnabled: this.featureToggleFacade.isFeatureAvailable$(FeatureFlag.A14MetricDrawer).pipe(take(1)),
      isA14GoalDrawerEnabled: this.featureToggleFacade.isFeatureAvailable$(FeatureFlag.A14GoalDrawer).pipe(take(1)),
    })
      .pipe(untilScopeDestroyed(this.$scope))
      .subscribe(({ isA14MetricDrawerEnabled, isA14GoalDrawerEnabled }) => {
        if (!isA14MetricDrawerEnabled && !isA14GoalDrawerEnabled) {
          return;
        }

        this.states = {
          msTeams: {
            goalSidebar: isA14GoalDrawerEnabled ? "gtmhub.msTeamsHome.dashboard._goals.goalNew" : "gtmhub.msTeamsHome.dashboard._goals._goalSidebar.goal",
            metric: isA14MetricDrawerEnabled ? "gtmhub.msTeamsHome.dashboard.metricNew" : "gtmhub.msTeamsHome.dashboard._metric.metric",
            metricDetails: isA14MetricDrawerEnabled
              ? "gtmhub.msTeamsHome.dashboard.metricNew._updateMetric.update"
              : "gtmhub.msTeamsHome.dashboard._metric.metric._updateMetric.update",
            createGoal: "gtmhub.msTeamsHome.dashboard._goals._goalForm.form",
          },
          goalSidebar: isA14GoalDrawerEnabled ? "gtmhub.home.dashboard._goals.goalNew" : "gtmhub.home.dashboard._goals._goalSidebar.goal",
          metric: isA14MetricDrawerEnabled ? "gtmhub.home.dashboard.metricNew" : "gtmhub.home.dashboard._metric.metric",
          metricDetails: isA14MetricDrawerEnabled ? "gtmhub.home.dashboard.metricNew._updateMetric.update" : "gtmhub.home.dashboard._metric.metric._updateMetric.update",
          createGoal: "gtmhub.home.dashboard._goals._goalForm.form",
        };
      });
  }

  private calculateSessionsPermissions(): SessionsPermissions {
    const accountPermissionsObjectives = this.accountService.getAccountSetting<AssignPermissions>("assignObjectivePermissions");
    const accountPermissionsKRs = this.accountService.getAccountSetting<AssignPermissions>("assignKRPermissions");

    if (this.$scope.activeSessionIds.length === 0) {
      return {
        permissionsObjectives: accountPermissionsObjectives,
        permissionsKRs: accountPermissionsKRs,
      };
    }

    // defaults for Objectives and KRs for each session and account
    const defaultPermissions = "user and team" as AssignPermissions;

    let permissionsObjectives = defaultPermissions;
    let permissionsKRs = defaultPermissions;

    // Count "Employees only" and "Teams only" permissions
    const permissionsObjectivesCount = {
      user: 0,
      team: 0,
    };
    const permissionsKRsCount = {
      user: 0,
      team: 0,
    };

    for (const [, session] of Object.entries(this.$scope.activeSessionsMap)) {
      const sessionPermissionsObjectives = session?.settings?.assignGoalPermissions || accountPermissionsObjectives;
      const sessionPermissionsKRs = session?.settings?.assignMetricPermissions || accountPermissionsKRs;

      switch (sessionPermissionsObjectives) {
        case "user":
          permissionsObjectivesCount.user++;
          break;

        case "team":
          permissionsObjectivesCount.team++;
          break;
      }

      switch (sessionPermissionsKRs) {
        case "user":
          permissionsKRsCount.user++;
          break;

        case "team":
          permissionsKRsCount.team++;
          break;
      }
    }

    const hasObjectivesOnlyUserPermissions = permissionsObjectivesCount.user === this.$scope.activeSessionIds.length;
    const hasKRsOnlyUserPermissions = permissionsKRsCount.user === this.$scope.activeSessionIds.length;

    const hasObjectivesOnlyTeamPermissions = permissionsObjectivesCount.team === this.$scope.activeSessionIds.length;
    const hasKRsOnlyTeamPermissions = permissionsKRsCount.team === this.$scope.activeSessionIds.length;

    // All sessions have "Employees only" permissions for objectives
    if (hasObjectivesOnlyUserPermissions) {
      permissionsObjectives = "user";
    }
    // All sessions have "Employees only" permissions for KRs
    if (hasKRsOnlyUserPermissions) {
      permissionsKRs = "user";
    }

    // All sessions have "Teams only" permissions for objectives
    if (hasObjectivesOnlyTeamPermissions) {
      permissionsObjectives = "team";
    }
    // All sessions have "Teams only" permissions for KRs
    if (hasKRsOnlyTeamPermissions) {
      permissionsKRs = "team";
    }

    return { permissionsObjectives, permissionsKRs };
  }

  private goToFeed = (): void => {
    this.homeService.notifyTabChanged(this.$rootScope, HomeScreenView.feed);
    this.$state.go("gtmhub.home.feed");
  };

  private scopeGoalsAndTrackScreen() {
    this.setGoalsToScope();
    this.trackScreenLoad();
  }

  private openQuantivePlusWizard = (): void => {
    const defaultSession = this.defaultSessionResolverService.getDefaultSession(this.$scope.activeSessions);
    this.ng1UiModalService.create<GuidedOKRsComponent, GuidedOKRsModalData>({
      uiContent: GuidedOKRsComponent,
      uiWrapClassName: "guided-okrs-modal-wrapper",
      uiWidth: "100%",
      uiFooter: null,
      uiClosable: false,
      uiKeyboard: false,
      uiData: {
        currentSession: defaultSession,
      },
    });
  };

  private checkIfCurrentUserOwnsMetricParent = (ownerIds: string[], params: { isTeamSection: boolean }): boolean => {
    if (params.isTeamSection) {
      return this.$scope.employee.teamIds.some((id) => ownerIds.includes(id)) || this.$scope.employee.managedTeamIds.some((id) => ownerIds.includes(id));
    }

    return ownerIds.includes(this.$scope.employee.id);
  };

  private onMyOkrsUpdated = (event: IAngularEvent, args: Record<"oldMyOkrs", IGoal[]>): void => {
    if (this.$scope.okrs && this.$scope.okrs.length > 0 && !equals(this.$scope.okrs, args.oldMyOkrs)) {
      this.setMetricsDeltas().then(() => {
        this.setGoalsToScope();
      });
    }

    if (!this.$scope.okrs || !this.$scope.okrs.length) {
      this.$scope.myOkrs = [];
      this.$scope.teamOkrs = [];
    }
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    switch (args.reason) {
      case "goalCreated":
        this.onGoalCreation(args.detail.goal);
        break;
      case "metricCreated":
        this.onMetricCreation(args.detail);
        break;
      case "metricMoved":
        this.onMetricMoved(args.detail);
        break;
      case "metricUpdated":
        this.onMetricUpdated(args.detail);
        break;
      case "metricPatched":
        this.onMetricPatched(args.detail.metric);
        break;
      case "metricDeleted":
        this.onMetricDeletion(args.detail.metricId, args.detail.goalId);
        break;
      case "goalPatched":
        this.onGoalPatched(args.detail.goal);
        break;
      case "goalChildReordered": {
        this.onGoalChildReordered(args.detail);
        break;
      }
    }
  };

  private onMetricPatched = (metric: Metric): void => {
    this.applyChangesToMetric(metric);
    this.setParentsToOkrs();
  };

  private onMetricUpdated = (result: { metric: Metric }): void => {
    this.applyChangesToMetric(result.metric);
    this.setParentsToOkrs();
  };

  private applyChangesToMetric = (metric: Metric): void => {
    const fields = "attainment,name,metrics{attainment,lastCheckInDate,name,ownerIds,latestSnapshotAt,id,sourceMetricId,goalId,orderId},ownerIds,dateFrom,dateTo";

    this.goalService
      .getGoalsV2({
        filter: {
          _id: metric.goalId,
        },
        fields,
      })
      .then(
        async (goalCollection) => {
          this.setMetricsDeltas();
          let goal: IHomeGoal;
          if (goalCollection.items.length === 0) {
            const shadowGoal = this.$scope.myOkrs.find((goal) => goal.id === "");
            const metrics = await this.getGoalWithoutPermissions(shadowGoal);
            goal = {
              id: "",
              metrics: metrics.items,
              ownerIds: [],
            };
          } else {
            goal = goalCollection.items[0];
          }
          this.updateEmployeeAndTeamsItems(metric, goal);
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
  };

  private updateGoalsCollection = (okrs: IGoal[], updatedGoal: IGoal, ownerId?: string): IGoal[] => {
    let updated = false;
    const isUpdatedGoalByOwner = this.checkIfOwnerOwnsItem(updatedGoal, ownerId);

    const updatedOkrs = okrs.map((goal) => {
      if (goal.id === updatedGoal.id) {
        goal = { ...goal, ...updatedGoal };
        updated = true;
      }
      return goal;
    });

    return !updated && isUpdatedGoalByOwner ? this.enrichGoals([...updatedOkrs, updatedGoal]) : this.enrichGoals(updatedOkrs);
  };

  private updateEmployeeAndTeamsItems = (metric: Metric, goal: IHomeGoal): void => {
    if (!metric || !goal) {
      return;
    }

    if (this.isEmployeeItem(metric) || this.isEmployeeItem(goal)) {
      const updatedGoal = this.getGoalById(goal, this.$scope.employee.id);
      this.$scope.myOkrs = this.updateGoalsCollection(this.$scope.myOkrs, updatedGoal);
    }

    if (this.isEmployeeTeamItem(metric) || this.isEmployeeTeamItem(goal)) {
      this.$scope.teamOkrs = this.$scope.teamOkrs.map((team) => {
        const updatedGoal = this.getGoalById(goal, team.id);
        team.goals = this.updateGoalsCollection(team.goals, updatedGoal);
        return team;
      });
    }

    if (!this.isEmployeeItem(metric) && !this.isEmployeeTeamItem(metric)) {
      this.removeMetric(metric.goalId, metric.id);
    }
  };

  private getGoalWithoutPermissions = (goal: IHomeGoal): IPromise<ICollection<Metric>> => {
    const metricIds = goal?.metrics.map((metric) => metric.id) || [];
    if (metricIds.length === 0) {
      return;
    }

    return this.metricService.getMetricsV2({
      filter: {
        _id: { $in: metricIds },
      },
      fields: "attainment,lastCheckInDate,name,ownerIds,latestSnapshotAt,id,sourceMetricId,goalId,orderId",
    });
  };

  private deleteFromCollection = <T extends IMyOKRs>(id: string, collection: T[]): T[] => {
    return collection.filter((item) => item.id !== id);
  };

  private openSessionSelectorModal = (team?: ITeam): void => {
    const newGoal = {
      id: null,
      teamId: team ? team.id : null,
      flowName: PiTrackingFlowNameEnum.CreateObjectiveFormHomeEmptyState,
    };

    this.analytics.track("Button Clicked", {
      button_name: "home_screen_empty_okr_list_create_okr",
      is_my_okrs: !team,
      is_team: !!team,
      team_name: team?.name || "",
      team_id: team?.id || "",
    });

    firstValueFrom(this.featureToggleFacade.isFeatureAvailable$(FeatureFlag.A14CreateObjectiveModal)).then((migratedCreateObjectiveModalEnabled) => {
      if (migratedCreateObjectiveModalEnabled) {
        this.createOkrFormModalService.openFormModal({
          flowName: PiTrackingFlowNameEnum.CreateObjectiveFormHomeEmptyState,
        });
      } else {
        const targetState = this.$rootScope.isMsTeamsState ? this.states.msTeams.createGoal : this.states.createGoal;
        this.$state.go(targetState, newGoal);
      }
    });
  };

  private strings = {
    owner: {
      user: "user",
      team: "team",
    },
  };

  private getDeltasMap = (data: IGoal[]): IDeltaMap => {
    return data
      ? data.reduce((map, el) => {
          map[el.id] = el;
          return map;
        }, {} as IDeltaMap)
      : {};
  };

  private isEmployeeItem = (item: IGoal | Metric): boolean => this.checkIfOwnerOwnsItem(item, this.$scope.employee.id);

  private isEmployeeTeamItem = (item: IGoal | Metric): boolean => {
    const teamIds = this.$scope.employee.managedTeamIds.concat(this.$scope.employee.teamIds).filter((teamId, index, self) => self.indexOf(teamId) === index);
    return item.ownerIds.some((ownerId) => teamIds.includes(ownerId));
  };

  private setMetricsDeltas = (): IPromise<void> => {
    const employeeIdAndTeamIds = [this.$scope.employee.id]
      .concat(this.$scope.employee.managedTeamIds, this.$scope.employee.teamIds)
      .filter((teamId, index, self) => self.indexOf(teamId) === index);

    return this.goalService
      .getGoalsAndMetricsDeltasByOwnerIds({ ownerIds: employeeIdAndTeamIds, sessionIds: this.$scope.activeSessionIds, period: "-14", excludeOrphaned: true })
      .then(
        (deltas) => {
          delete this.$scope.indicators.loading;
          this.$scope.goalsAndMetricDeltasMap = this.getDeltasMap(deltas.goals.concat(deltas.metrics));
        },
        (error) => {
          this.$scope.indicators.loading = { error };
        }
      );
  };

  private setGoalsToScope = (): void => {
    const enrichedGoals = this.enrichGoals(this.$scope.okrs);

    this.$scope.myOkrs = this.getMyOkrs(enrichedGoals);
    this.$scope.teamOkrs = this.getTeamOkrs(enrichedGoals, this.$scope.teams);

    this.$scope.hasAnyUserOKRs = this.hasUserOKRs();
    this.$scope.hasAnyTeamOKRs = this.hasTeamOKRs();

    this.setParentsToOkrs();
  };

  private setParentsToOkrs() {
    const homeOkrs = [...this.$scope.teamOkrs.flatMap((team) => team.goals), ...this.$scope.myOkrs];
    this.lookForParentsToOkrs(homeOkrs, this.mongoFilters.goalFilters);
    this.lookForParentsToOkrs(homeOkrs, this.mongoFilters.metricFilters);
  }

  private lookForParentsToOkrs = (goals: IHomeGoal[], parent: IParentFilter): void => {
    const okrsWithParentsIds = goals.filter((okr) => okr?.parentId && okr?.parentType === parent.type).map((okr) => okr.parentId);
    if (okrsWithParentsIds.length === 0) {
      return;
    }

    const mongoGoalFilter = {
      filter: { _id: { $in: okrsWithParentsIds } },
      fields: parent.fields,
    };

    if (parent.type === "goal") {
      this.goalService.getGoalsV2(mongoGoalFilter).then(
        (parents) => {
          this.setObjectiveParentInfoToGoal(parents.items, goals);
        },
        (error) => (this.$scope.indicators.loading = { error })
      );

      return;
    }

    this.metricService.getMetricsV2(mongoGoalFilter).then(
      (parents) => {
        this.setKeyResultParentInfoToGoal(parents.items, goals);
      },
      (error) => (this.$scope.indicators.loading = { error })
    );
  };

  private setObjectiveParentInfoToGoal = (parents: IGoal[], goals: IHomeGoal[]): void => {
    parents.forEach((parentGoal) => {
      const childGoals = goals.filter((x) => x?.parentId === parentGoal.id);

      childGoals.forEach((childGoal) => {
        this.setParentToChild(childGoal, parentGoal);
      });
    });
  };

  private setKeyResultParentInfoToGoal = (parents: Metric[], goals: IHomeGoal[]): void => {
    const objectiveToKeyResultIds = parents.filter((x) => x?.goalId).map((x) => x.goalId);
    if (objectiveToKeyResultIds.length === 0) {
      return;
    }

    const mongoGoalFilter = {
      filter: { _id: { $in: objectiveToKeyResultIds } },
      fields: "private",
    };

    this.goalService.getGoalsV2(mongoGoalFilter).then(
      (goalsOfKR) => {
        parents.forEach((parentKR) => {
          const childGoals = goals.filter((x) => x?.parentId === parentKR.id);
          const goalOfchild = goalsOfKR.items.find((x) => x?.id === parentKR.goalId) as IGoal;

          childGoals.forEach((childGoal) => {
            childGoal.isKRGoalPrivate = goalOfchild.private;
            this.setParentToChild(childGoal, parentKR);
          });
        });
      },
      (error) => (this.$scope.indicators.loading = { error })
    );
  };

  private setParentToChild(child: IHomeGoal, parent: IMyOKRs) {
    child.isCurrentUserTheOwnerOfParent =
      parent.ownerIds.includes(this.$scope.employee.id) ||
      this.$scope.employee?.teamIds.some((id) => parent.ownerIds.includes(id)) ||
      this.$scope.employee?.managedTeamIds.some((id) => parent.ownerIds.includes(id));

    if (!child.isCurrentUserTheOwnerOfParent) {
      child.parent = parent;
    }
  }

  private trackScreenLoad = (): void => {
    const homeMetadata = this.getHomeMetadata(this.$scope.activeSessions, this.$scope.myOkrs, this.$scope.teamOkrs);
    this.analytics.track("Home Data Loaded", homeMetadata);
  };

  private getHomeMetadata = (sessions: Session[], myOkrs: IHomeGoal[], teamOkrs: ITeamOkr[]) => {
    // filter in order to get only relevant information about my or team okrs
    // hide goal is set when the goal is owned by somewhere else than the current user or team
    const filteredMyOkrs = myOkrs.filter((goal) => !goal.hideGoal);
    const filteredTeamOkrs = teamOkrs.map((team) => team.goals.filter((goal) => !goal.hideGoal));

    const teamKrsCount = filteredTeamOkrs.reduce((acc, goals) => {
      return (acc += goals.reduce((metricsCnt, goal) => {
        return (metricsCnt += goal.metrics.length);
      }, 0));
    }, 0);

    return {
      sessions: sessions.length,
      my_krs: filteredMyOkrs.reduce((acc, goal) => (acc += goal.metrics.length), 0),
      my_goals: filteredMyOkrs.length,
      team_krs: teamKrsCount,
      team_goals: filteredTeamOkrs.reduce((acc, goal) => (acc += goal.length), 0),
      state: "dashboard",
    };
  };

  private enrichGoals = (goals: IGoal[]): IGoal[] => {
    return goals.map((goal) => {
      return {
        ...goal,
        color: this.progressColoring.getColorOrGeneric(this.homeService.roundProgressValue(goal.attainment)),
        metrics: goal.metrics
          ? goal.metrics.map((metric) => {
              return {
                ...metric,
                deadline: this.homeService.calcDeadline(metric.lastCheckInDate),
                color: this.progressColoring.getColorOrGeneric(this.homeService.roundProgressValue(metric.attainment)),
              };
            })
          : [],
      };
    });
  };

  private getMyOkrs = (goals: IGoal[]): IGoal[] => {
    return goals
      .filter((goal) => this.filterGoalsByGoalAndMetricOwner(goal, this.$scope.employee.id))
      .map((goal) => {
        return this.getGoalById(goal, this.$scope.employee.id);
      });
  };

  private getTeamOkrs = (goals: IGoal[], teams: ITeam[]): ITeamOkr[] => {
    return teams.map((team) => {
      const teamGoals = goals
        .filter((goal) => this.filterGoalsByGoalAndMetricOwner(goal, team.id))
        .map((goal) => {
          return this.getGoalById(goal, team.id);
        });

      return { ...team, goals: teamGoals || [] };
    });
  };

  private filterGoalsByGoalAndMetricOwner = (goal: IGoal, ownerId: string): boolean => {
    const goalOwner = this.checkIfOwnerOwnsItem(goal, ownerId);
    const metricOwner = goal.metrics.some((metric) => this.checkIfOwnerOwnsItem(metric, ownerId));

    return goalOwner || metricOwner;
  };

  private getGoalById = (goal: IGoal, ownerId: string): IHomeGoal => {
    const metrics = this.getMetricsById(goal.metrics, ownerId);

    return { ...goal, hideGoal: !this.checkIfOwnerOwnsItem(goal, ownerId), metrics: metrics };
  };

  private getMetricsById = (metrics: Metric[], employeeId: string): Metric[] => {
    return metrics.filter((metric) => this.checkIfOwnerOwnsItem(metric, employeeId));
  };

  private openGoal = (goal: IGoal): void => {
    if (this.$rootScope.isMsTeamsState) {
      this.$state.go(this.states.msTeams.goalSidebar, { id: goal.id });
      return;
    }

    this.$state.go(this.states.goalSidebar, { id: goal.id });
  };

  private openMetric = (metric: Metric): void => {
    if (this.$rootScope.isMsTeamsState) {
      this.$state.go(this.states.msTeams.metric, { metricId: metric.id });

      return;
    }

    this.$state.go(this.states.metric, { metricId: metric.id });
  };

  private openUpdateMetricState = (goal: IGoal, metric: Metric): void => {
    this.updateMetricModalService
      .openUpdateMetricModal({
        metricId: metric.id,
      })
      .pipe(take(1))
      .subscribe();
  };

  private toggleSection = (owner: "user" | "team", teamId?: string): void => {
    if (owner === "user") {
      this.$scope.myOkrsCollapsed = !this.$scope.myOkrsCollapsed;
      this.$scope.updateCollapse({ item: { myOkrs: this.$scope.myOkrsCollapsed } });
    } else {
      this.$scope.myTeamsOkrsCollapsed[teamId] = !this.$scope.myTeamsOkrsCollapsed[teamId];
      this.$scope.updateCollapse({ item: { myTeamsOkrs: this.$scope.myTeamsOkrsCollapsed } });
    }
  };

  private getDefaultDeltaMapValue = (): IDeltaMap => {
    return {
      attainmentDelta: {
        current: 0,
        delta: 0,
        deltaPercentage: 0,
        previous: 0,
      },
    } as IDeltaMap;
  };

  private onGoalCreation = (newGoal: IGoal): void => {
    const now = dayjs.utc();

    if (!now.isSameOrBefore(newGoal.dateTo) || !now.isSameOrAfter(newGoal.dateFrom)) return;

    const newGoalEnriched = this.enrichGoals([newGoal])[0];

    this.$scope.goalsAndMetricDeltasMap[newGoalEnriched.id] = this.getDefaultDeltaMapValue();

    if (this.isEmployeeItem(newGoal)) {
      this.$scope.myOkrs = this.getMyOkrs([...this.$scope.myOkrs, newGoalEnriched]);
      this.$scope.hasAnyUserOKRs = this.hasUserOKRs();
    }

    if (this.isEmployeeTeamItem(newGoal)) {
      const teamOkrsMap = toIdMap(this.$scope.teamOkrs.flatMap((x) => x.goals));
      this.$scope.teamOkrs = this.getTeamOkrs([...Object.values(teamOkrsMap), newGoalEnriched], this.$scope.teams);
      this.$scope.hasAnyTeamOKRs = this.hasTeamOKRs();
    }
  };

  private onMetricCreation = ({ metric }: { metric: Metric; goalId?: string }): void => {
    this.$scope.goalsAndMetricDeltasMap[metric.id] = this.getDefaultDeltaMapValue();

    this.addMetric(metric);
  };

  private addMetric = (metric: Metric): void => {
    const foundGoal = this.isEmployeeItem(metric) ? this.$scope.myOkrs.find((goal) => goal.id === metric.goalId) : null;
    if (foundGoal) {
      if (foundGoal.metrics) {
        foundGoal.metrics.push(metric);
      } else {
        foundGoal.metrics = [metric];
      }
      this.$scope.myOkrs = this.updateGoalsCollection(this.$scope.myOkrs, foundGoal);
    }

    const foundTeam = this.isEmployeeTeamItem(metric)
      ? this.$scope.teamOkrs.find((team) => this.checkIfOwnerOwnsItem(metric, team.id) && team.goals.find((goal) => goal.id === metric.goalId))
      : null;

    if (foundTeam) {
      foundTeam.goals = foundTeam.goals ? foundTeam.goals : [];
      const foundTeamGoal = foundTeam.goals.find((goal) => goal.id === metric.goalId);

      if (foundTeamGoal) {
        if (foundTeamGoal.metrics) {
          foundTeamGoal.metrics.push(metric);
        } else {
          foundTeamGoal.metrics = [metric];
        }
        this.$scope.teamOkrs = this.updateGoalsCollection(this.$scope.teamOkrs, foundTeamGoal) as ITeamOkr[];
      }
    }
  };

  private onGoalDeletion = (event: IAngularEvent, deletedGoalId: string, deletedGoal: IGoal): void => {
    // Employee's goal
    if (this.isEmployeeItem(deletedGoal) || deletedGoal.metrics?.some((metric) => this.checkIfOwnerOwnsItem(metric, this.$scope.employee.id))) {
      this.$scope.myOkrs = this.deleteFromCollection<IGoal>(deletedGoalId, this.$scope.myOkrs);
      this.$scope.hasAnyUserOKRs = this.hasUserOKRs();
    }

    // Teams' goal
    this.$scope.teamOkrs.forEach((team) => {
      team.goals = this.deleteFromCollection<IGoal>(deletedGoalId, team.goals);
      this.$scope.hasAnyTeamOKRs = this.hasTeamOKRs();
    });
  };

  private onMetricDeletion = (metricId: string, goalId: string): void => {
    this.removeMetric(goalId, metricId);
  };

  private onMetricMoved = ({ goalId, metric }: { goalId: string; metric: Metric }) => {
    this.removeMetric(goalId, metric.id);
    this.addMetric(metric);
  };

  private removeMetric = (goalId: string, metricId: string): void => {
    // Employee's metric
    this.$scope.myOkrs.forEach((goal) => {
      if (goal.id === goalId) {
        goal.metrics = this.deleteFromCollection<Metric>(metricId, goal.metrics);
        if (!this.checkIfOwnerOwnsItem(goal, this.$scope.employee.id) && goal.metrics.length === 0) {
          this.$scope.myOkrs = this.deleteFromCollection<IGoal>(goal.id, this.$scope.myOkrs);
        }
      }
    });

    // Teams' metric
    this.$scope.teamOkrs.forEach((team) => {
      team.goals.forEach((goal) => {
        if (goal.id === goalId) {
          goal.metrics = this.deleteFromCollection<Metric>(metricId, goal.metrics);
          if (!this.checkIfOwnerOwnsItem(goal, team.id) && goal.metrics.length === 0) {
            team.goals = this.deleteFromCollection<IGoal>(goal.id, team.goals);
          }
        }
      });
    });
  };

  private isCollapsed = (params: { owner: string; isMyListCollapsed: boolean; isTeamListCollapsed: boolean }): boolean => {
    return (params.owner === "user" && params.isMyListCollapsed) || (params.owner === "team" && params.isTeamListCollapsed);
  };

  private onGoalPatched = (editedGoal: IGoal) => {
    const editedGoalEnriched = {
      ...editedGoal,
      color: this.progressColoring.getColorOrGeneric(this.homeService.roundProgressValue(editedGoal.attainment)),
    };

    if (!this.isEmployeeItem(editedGoal)) {
      this.$scope.myOkrs = this.deleteFromCollection(editedGoal.id, this.$scope.myOkrs);
    }

    this.$scope.teamOkrs = this.$scope.teamOkrs.map((team) => {
      if (!this.checkIfOwnerOwnsItem(editedGoal, team.id)) {
        team.goals = this.deleteFromCollection(editedGoal.id, team.goals);
      }

      return team;
    });

    // Employee's goal
    if (this.isEmployeeItem(editedGoal)) {
      this.$scope.myOkrs = this.updateGoalsCollection(this.$scope.myOkrs, editedGoalEnriched, this.$scope.employee.id);
    }

    // Teams' goal
    if (this.isEmployeeTeamItem(editedGoal)) {
      this.$scope.teamOkrs = this.$scope.teamOkrs.map((team) => {
        team.goals = this.updateGoalsCollection(team.goals, editedGoalEnriched, team.id);
        return team;
      });
    }

    this.setParentsToOkrs();
  };

  private updateGoalMetricIfFound = (detail: IGoalChildReorderedEventDetail, goal: IHomeGoal) => {
    return goal.metrics.some((metric) => {
      if (metric.id === detail.childId) {
        this.applyChangesToMetric({ ...metric, orderId: detail.childOrderId });
        return true;
      }
      return false;
    });
  };

  private onGoalChildReordered = (detail: IGoalChildReorderedEventDetail) => {
    // Currently the Home screen does not support nested contributors of type 'goal'
    // Updates are applied only if the reordered contributor is of type 'metric'
    if (detail.childType === "goal") return;

    let isMetricFound = false;
    this.$scope.myOkrs.some((okr) => {
      isMetricFound = this.updateGoalMetricIfFound(detail, okr);
      return isMetricFound;
    });

    if (isMetricFound) return;

    this.$scope.teamOkrs.some((team) => {
      return team.goals.some((goal) => {
        isMetricFound = this.updateGoalMetricIfFound(detail, goal);
        return isMetricFound;
      });
    });
  };

  private hasUserOKRs(): boolean {
    return this.$scope.myOkrs && this.$scope.myOkrs.length > 0;
  }

  private hasTeamOKRs(): boolean {
    return this.$scope.teamOkrs && this.$scope.teamOkrs.length > 0;
  }

  private checkIfOwnerOwnsItem = (item: IHomeGoal | Metric, ownerId: string): boolean => item.ownerIds?.includes(ownerId);

  public static factory(): IDirectiveFactory {
    const directive = (
      homeService: HomeService,
      uiErrorHandlingService: UIErrorHandlingService,
      $state: StateService,
      goalService: GoalService,
      $rootScope: ITraceRootScopeService,
      analytics: AnalyticsService,
      progressColoring: ProgressColoring,
      metricService: MetricService,
      accountService: AccountService,
      apmSpanService: ApmSpanService,
      ng1UiModalService: Ng1UiModalService,
      defaultSessionResolverService: DefaultSessionResolverService,
      piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
      updateMetricModalService: UpdateMetricModalService,
      featureTogglesFacade: FeatureTogglesFacade,
      createOkrFormModalService: CreateOkrFormModalService
    ) =>
      new HomeMyOkrs(
        homeService,
        uiErrorHandlingService,
        $state,
        goalService,
        $rootScope,
        analytics,
        progressColoring,
        metricService,
        accountService,
        apmSpanService,
        ng1UiModalService,
        defaultSessionResolverService,
        piFeatureVisibilityFacade,
        updateMetricModalService,
        featureTogglesFacade,
        createOkrFormModalService
      );

    directive.$inject = [
      "HomeService",
      "UIErrorHandlingService",
      "$state",
      "GoalService",
      "$rootScope",
      "AnalyticsService",
      "ProgressColoring",
      "MetricService",
      "AccountService",
      "ApmSpanService",
      "Ng1UiModalService",
      "DefaultSessionResolverService",
      "PiFeatureVisibilityFacade",
      "UpdateMetricModalService",
      "FeatureTogglesFacade",
      "CreateOkrFormModalService",
    ];
    return directive;
  }
}
